/**
 * Descarga de tablas como planilla .xlsx de verdad.
 *
 * POR QUE NO CSV
 *
 * El CSV se veia mal al abrirlo: Excel usa como separador de lista el que
 * define la configuracion regional, y en español es el PUNTO Y COMA. Un CSV
 * separado por comas se abre entonces con todo apilado en una sola columna, y
 * hay que entrar al asistente de importacion para arreglarlo. Encima los
 * numeros llegan como texto, asi que no se pueden ordenar ni sumar.
 *
 * Un .xlsx no tiene separadores: los tipos viajan dentro del archivo. Abre
 * bien en cualquier idioma, en Excel, en Google Sheets y en LibreOffice.
 *
 * QUE AGREGA ADEMAS
 *
 *   - Numeros y fechas como numeros y fechas, no como texto. Se ordenan y se
 *     suman sin tocar nada.
 *   - Ancho de columna por columna, para que no queden los ###.
 *   - Autofiltro en la fila de titulos.
 *   - Varias hojas en un solo archivo cuando la pantalla tiene varias vistas.
 *
 * La libreria se carga en el momento de descargar (`import()` dinamico) y no
 * en el bundle principal: pesa, y la mayoria de las visitas nunca descarga.
 */

/** Formatos de celda por tipo de columna. */
const FORMATOS = {
  numero: '0',
  decimal: '0.0',
  porcentaje: '0.0"%"',
  horas: '0.0" h"',
  fecha: 'dd/mm/yyyy',
  texto: null
}

/**
 * @param {string} nombreArchivo  sin extension; se le agrega la fecha y .xlsx
 * @param {Array<{
 *   nombre: string,
 *   columnas: Array<{clave: string, titulo: string, ancho?: number, tipo?: string}>,
 *   filas: Array<Object>
 * }>} hojas
 */
export async function descargarExcel (nombreArchivo, hojas) {
  const XLSX = await import(/* webpackChunkName: "xlsx" */ 'xlsx')

  const libro = XLSX.utils.book_new()

  for (const hoja of hojas) {
    const titulos = hoja.columnas.map(c => c.titulo)

    const datos = hoja.filas.map(fila => {
      const salida = {}

      for (const col of hoja.columnas) {
        const valor = fila[col.clave]
        // null es "sin medicion" en todas estas pantallas. Va como celda vacia
        // y no como 0: un 0 en una planilla que alguien va a promediar despues
        // es peor que un hueco.
        salida[col.titulo] = valor === null || valor === undefined ? '' : valor
      }

      return salida
    })

    const ws = XLSX.utils.json_to_sheet(datos, { header: titulos })

    ws['!cols'] = hoja.columnas.map(c => ({ wch: c.ancho || 16 }))

    aplicarFormatos(XLSX, ws, hoja.columnas, hoja.filas.length)

    // Sin congelar la fila de titulos: se probo y la version comunitaria de
    // SheetJS no escribe el <pane> en el XML, asi que dejarlo puesto seria
    // codigo que no hace nada. El autofiltro de abajo si funciona.
    if (hoja.filas.length > 0) {
      ws['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: hoja.filas.length, c: hoja.columnas.length - 1 }
        })
      }
    }

    // Excel no admite nombres de hoja de mas de 31 caracteres ni estos signos.
    const nombreHoja = String(hoja.nombre).replace(/[\\/?*[\]:]/g, ' ').substring(0, 31)

    XLSX.utils.book_append_sheet(libro, ws, nombreHoja)
  }

  const fecha = new Date().toISOString().substring(0, 10)

  XLSX.writeFile(libro, `${nombreArchivo}_${fecha}.xlsx`)
}

/**
 * Marca el formato de cada celda segun el tipo declarado de su columna.
 *
 * Se hace despues de `json_to_sheet` y no antes porque esa funcion arma las
 * celdas desde cero: cualquier `z` puesto previamente se pierde.
 */
function aplicarFormatos (XLSX, ws, columnas, totalFilas) {
  columnas.forEach((col, indiceCol) => {
    const formato = FORMATOS[col.tipo]

    if (!formato) {
      return
    }

    for (let fila = 1; fila <= totalFilas; fila++) {
      const celda = ws[XLSX.utils.encode_cell({ r: fila, c: indiceCol })]

      // Solo a las que quedaron numericas. Una celda vacia (el "sin medicion")
      // no lleva formato: si lo llevara, Excel mostraria "0.0%" en un hueco.
      if (celda && celda.t === 'n') {
        celda.z = formato
      }
    }
  })
}
